import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, PENDING_AVAILABILITY_KEY, PENDING_ZIP_KEY } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import GoogleButton, { useGoogleEnabled } from "../components/GoogleButton";
import { safeNext } from "../lib/safeNext";

export default function Signup({ embedded = false }) {
  // Availability emails are opt-in only: the box always starts unchecked.
  const [notify, setNotify] = useState(false);
  const [zipCode, setZipCode] = useState(() => sessionStorage.getItem(PENDING_ZIP_KEY) || "");
  useEffect(() => { sessionStorage.removeItem(PENDING_AVAILABILITY_KEY); }, []);
  const { signup } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: ""
  });

  const linkToken = null;
  const googleOn = useGoogleEnabled();
  const from = (() => {
    const q = new URLSearchParams(location.search).get("redirect");
    const raw = q || location.state?.from || "/dashboard";
    // Only accept safe internal paths
    return safeNext(raw) || "/dashboard";
  })();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Oops! Your passwords don't match.");
      return;
    }

    if (zipCode && !/^\d{5}$/.test(zipCode)) {
      toast.error("Please enter a 5-digit ZIP, or leave it blank.");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Please use a password with at least 6 characters.");
      return;
    }

    setLoading(true);
    
    try {
      await signup(formData.email, formData.password, formData.firstName, formData.lastName, { zipCode, notify });
      toast.success("Welcome to American Farmstand!");
      navigate(from.startsWith("/welcome") ? from : `/welcome?next=${encodeURIComponent(from === "/dashboard" ? "/marketplace/map" : from)}`, { replace: true });
    } catch (error) {
      const message = error.response?.data?.detail || "Something went wrong. Please try again!";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={embedded ? "bg-white text-left" : "min-h-screen bg-white pt-20 lg:pt-24 flex items-center"} data-testid="signup-page">
      <div className={embedded ? "w-full mx-auto pt-8" : "w-full max-w-sm mx-auto px-6 py-16"}>
        <div className="text-center mb-10">
          <h1 
            className="text-2xl lg:text-3xl mb-3"
            style={{ color: '#0f1d2f', fontFamily: "'GFS Didot', 'Didot', Georgia, serif" }}
            data-testid="signup-heading"
          >
            {embedded ? "Your American Farmstand experience starts here." : "Discover American Farmstand"}
          </h1>
          <p className="text-sm leading-relaxed" style={{ color: '#0f1d2f' }} data-testid="signup-intro">
            {embedded ? "Enjoy a complimentary account to discover farms, farmers markets and farmstands, save your favorite places, and build your personal wishlist—all while farm-direct shopping grows in your area." : "Your gateway to independent farms, farmers markets, and seasonal discoveries across America."}
          </p>
        </div>

        <div
          className="border p-5 mb-8"
          style={{ borderColor: 'rgba(30, 58, 95, 0.15)' }}
          data-testid="signup-benefits-preview"
        >
          <span
            className="text-[10px] tracking-widest uppercase block mb-3"
            style={{ color: '#B6A46D', letterSpacing: '1.8px' }}
          >
            Your free account includes
          </span>
          <ul
            className="text-sm leading-relaxed space-y-1.5"
            style={{ color: '#0f1d2f', opacity: 0.82, listStyle: 'none', paddingLeft: 0 }}
          >
            <li>Discover independent farms and farmstands nationwide</li>
            <li>Explore farmers markets across America</li>
            <li>Save your favorite places and create your Wish List</li>
            <li>Explore the Seasonal Field Guide</li>
            <li>Receive regional updates as participating farms become available</li>
          </ul>
        </div>

        {!linkToken && <GoogleButton next={(from === "/dashboard" ? "" : from)} testid="signup-google-btn" />}
        {!linkToken && googleOn && (
        <div className="flex items-center gap-3 my-6" aria-hidden="true">
          <span className="flex-1 h-px" style={{ backgroundColor: 'rgba(15,29,47,0.12)' }} />
          <span className="text-[10px] tracking-widest uppercase" style={{ color: '#0f1d2f', opacity: 0.5 }}>or continue with email</span>
          <span className="flex-1 h-px" style={{ backgroundColor: 'rgba(15,29,47,0.12)' }} />
        </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs tracking-widest uppercase mb-2" style={{ color: '#0f1d2f' }}>
                First Name
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                data-testid="signup-firstname-input"
              />
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase mb-2" style={{ color: '#0f1d2f' }}>
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                data-testid="signup-lastname-input"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase mb-2" style={{ color: '#0f1d2f' }}>
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              data-testid="signup-email-input"
            />
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase mb-2" style={{ color: '#0f1d2f' }}>
              Password
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              data-testid="signup-password-input"
            />
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase mb-2" style={{ color: '#0f1d2f' }}>
              Confirm Password
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              data-testid="signup-confirm-password-input"
            />
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase mb-2" style={{ color: '#0f1d2f' }}>
              ZIP Code <span style={{ opacity: 0.5 }}>(optional)</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={5}
              name="zipCode"
              value={zipCode}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 5);
                setZipCode(v);
                if (/^\d{5}$/.test(v)) sessionStorage.setItem(PENDING_ZIP_KEY, v);
                else sessionStorage.removeItem(PENDING_ZIP_KEY);
              }}
              data-testid="signup-zip-input"
            />
          </div>
          <label className="flex items-start gap-3 text-sm leading-relaxed" style={{ color: '#0f1d2f' }}>
            <input type="checkbox" checked={notify} onChange={(e) => {
              setNotify(e.target.checked);
              if (e.target.checked) sessionStorage.setItem(PENDING_AVAILABILITY_KEY, "true");
              else sessionStorage.removeItem(PENDING_AVAILABILITY_KEY);
            }} className="mt-1 shrink-0" style={{ width: 16, height: 16 }} data-testid="free-signup-notify" />
            <span>Email me when farm-direct shopping becomes available in my area. Unsubscribe anytime.</span>
          </label>
          <button 
            type="submit" 
            className="btn-primary w-full"
            disabled={loading}
            data-testid="signup-submit-btn"
          >
            {loading ? "Creating your account..." : embedded ? "Create Your Free Account" : "Create Account"}
          </button>
        </form>

        {embedded && <p className="text-center text-xs mt-4">Free to join. No payment details required.</p>}
        <p className="text-center mt-10 text-sm" style={{ color: '#0f1d2f' }}>
          Already have an account?{" "}
          <Link to="/login" className="underline" data-testid="login-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
